import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatCurrency } from "@/lib/api";
import { GitCompare, ArrowRight, CalendarClock, ArrowUpRight, ArrowDownRight, Users, Repeat } from "lucide-react";

const CATEGORY_LABELS = {
  nomina: "Nómina", hipoteca: "Hipoteca", alquiler: "Alquiler", servicios: "Servicios",
  supermercado: "Supermercado", restaurantes: "Restaurantes", transporte: "Transporte",
  gasolina: "Gasolina", salud: "Salud", ocio: "Ocio", viajes: "Viajes", compras: "Compras",
  educacion: "Educación", impuestos: "Impuestos", transferencias: "Transferencias",
  ingresos: "Otros ingresos", cripto: "Cripto", inversiones: "Inversiones", otros: "Otros",
};

const isoDate = (d) => d.toISOString().slice(0, 10);
const today = () => new Date();
const daysAgo = (n) => {
  const d = today();
  d.setDate(d.getDate() - n);
  return d;
};
const startOfYear = () => new Date(today().getFullYear(), 0, 1);
// Month helpers (local time)
const startOfMonth = (offset = 0) => {
  const d = today();
  return new Date(d.getFullYear(), d.getMonth() + offset, 1);
};
const endOfMonth = (offset = 0) => {
  const d = today();
  return new Date(d.getFullYear(), d.getMonth() + offset + 1, 0);
};
const monthLabel = (offset = 0) => {
  const d = startOfMonth(offset);
  return d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
};

const RANGE_PRESETS = [
  { id: "30d", label: "30d", get: () => ({ start: isoDate(daysAgo(30)), end: isoDate(today()) }) },
  { id: "90d", label: "90d", get: () => ({ start: isoDate(daysAgo(90)), end: isoDate(today()) }) },
  { id: "1a", label: "1a", get: () => ({ start: isoDate(daysAgo(365)), end: isoDate(today()) }) },
  { id: "ytd", label: "Año", get: () => ({ start: isoDate(startOfYear()), end: isoDate(today()) }) },
  { id: "all", label: "Todo", get: () => ({ start: "", end: "" }) },
];

function DateRangePicker({ preset, onPreset, start, end, onCustom }) {
  return (
    <div className="rounded-xl border border-[#EBEBEA] bg-[#FAFAF9] p-3 flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2 text-[#5F7368] text-xs font-medium">
        <CalendarClock className="w-3.5 h-3.5" />
        Rango
      </div>
      <div className="flex items-center gap-1" data-testid="compare-date-presets">
        {RANGE_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            data-testid={`compare-range-${p.id}`}
            onClick={() => onPreset(p.id)}
            className={`h-7 px-3 rounded-lg text-xs font-medium transition-colors ${
              preset === p.id ? "bg-[#0F291E] text-white" : "bg-white text-[#5F7368] hover:bg-[#EBEBEA] border border-[#EBEBEA]"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1 text-xs">
        <input
          type="date"
          data-testid="compare-range-start"
          value={start}
          onChange={(e) => onCustom({ start: e.target.value, end })}
          className="h-7 px-2 rounded-md border border-[#EBEBEA] bg-white text-xs text-[#0A110E] focus:outline-none focus:border-[#1C7C54]/60"
        />
        <span className="text-[#8A9992]">→</span>
        <input
          type="date"
          data-testid="compare-range-end"
          value={end}
          onChange={(e) => onCustom({ start, end: e.target.value })}
          className="h-7 px-2 rounded-md border border-[#EBEBEA] bg-white text-xs text-[#0A110E] focus:outline-none focus:border-[#1C7C54]/60"
        />
      </div>
    </div>
  );
}

// Fetches summary for one account+range. Returns state {data, loading}.
function useSummary(accountId, range) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!accountId) { setData(null); return; }
    const params = { account_id: accountId };
    if (range?.start) params.start_date = range.start;
    if (range?.end) params.end_date = range.end;
    let cancelled = false;
    api.get("/transactions/summary", { params })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [accountId, range?.start, range?.end]);
  return data;
}

function CategoryList({ byCat, currency }) {
  const items = useMemo(() => {
    if (!byCat) return [];
    return Object.entries(byCat).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [byCat]);
  if (items.length === 0) return null;
  return (
    <div className="rounded-xl border border-[#EBEBEA] p-4">
      <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992] mb-2">Top categorías</div>
      <ul className="space-y-1.5">
        {items.map(([c, v]) => (
          <li key={c} className="flex items-center justify-between text-sm">
            <span className="text-[#0A110E]">{CATEGORY_LABELS[c] || c}</span>
            <span className="tabular-nums text-[#5F7368]">{formatCurrency(v, currency)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SummaryCards({ acc, data, showBalance = true }) {
  return (
    <>
      {showBalance && (
        <div className="rounded-xl border border-[#EBEBEA] p-4">
          <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Saldo actual</div>
          <div className="font-display text-2xl font-semibold tabular-nums text-[#0A110E]">{formatCurrency(acc.balance, acc.currency)}</div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-[#EBEBEA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Ingresos</div>
          <div className="font-display text-lg font-semibold tabular-nums text-[#1C7C54]">
            {formatCurrency(data?.income || 0, acc.currency)}
          </div>
        </div>
        <div className="rounded-xl border border-[#EBEBEA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Gastos</div>
          <div className="font-display text-lg font-semibold tabular-nums text-[#D95D39]">
            {formatCurrency(data?.expense || 0, acc.currency)}
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-[#EBEBEA] px-3 py-2">
        <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Movimientos</div>
        <div className="font-display text-lg font-semibold tabular-nums text-[#0A110E]">
          {data?.count ?? 0}
        </div>
      </div>
      <CategoryList byCat={data?.by_category} currency={acc.currency} />
    </>
  );
}

function AccountColumn({ accounts, selected, onSelect, label, range }) {
  const data = useSummary(selected, range);
  const acc = accounts.find((a) => a.id === selected);
  return (
    <div className="flex flex-col gap-4 flex-1 min-w-0">
      <div>
        <div className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992] mb-1">{label}</div>
        <Select value={selected || ""} onValueChange={onSelect}>
          <SelectTrigger data-testid={`compare-${label.toLowerCase()}`}><SelectValue placeholder="Selecciona cuenta" /></SelectTrigger>
          <SelectContent>
            {accounts.map((a) => (
              <SelectItem key={a.id} value={a.id}>{a.bank} · {a.alias}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {!acc ? (
        <div className="rounded-xl border border-dashed border-[#EBEBEA] p-8 text-center text-sm text-[#8A9992]">
          Elige una cuenta
        </div>
      ) : (
        <SummaryCards acc={acc} data={data} />
      )}
    </div>
  );
}

// --- Month vs Month mode ---
function DeltaBadge({ current, previous, testid }) {
  if (previous === 0 || previous == null) {
    return <span className="text-[11px] text-[#8A9992]" data-testid={testid}>—</span>;
  }
  const pct = ((current - previous) / previous) * 100;
  const up = pct >= 0;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] font-semibold ${
        up ? "bg-[#1C7C54]/10 text-[#1C7C54]" : "bg-[#D95D39]/10 text-[#D95D39]"
      }`}
    >
      {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {up ? "+" : ""}{pct.toFixed(1)}%
    </span>
  );
}

function MonthVsMonthView({ accounts, selected, onSelect }) {
  const acc = accounts.find((a) => a.id === selected);
  const rangeCurrent = useMemo(() => ({
    start: isoDate(startOfMonth(0)),
    end: isoDate(endOfMonth(0)),
  }), []);
  const rangePrev = useMemo(() => ({
    start: isoDate(startOfMonth(-1)),
    end: isoDate(endOfMonth(-1)),
  }), []);
  const curData = useSummary(selected, rangeCurrent);
  const prevData = useSummary(selected, rangePrev);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992] mb-1">Cuenta</div>
        <Select value={selected || ""} onValueChange={onSelect}>
          <SelectTrigger data-testid="compare-mvm-account"><SelectValue placeholder="Selecciona cuenta" /></SelectTrigger>
          <SelectContent>
            {accounts.map((a) => (
              <SelectItem key={a.id} value={a.id}>{a.bank} · {a.alias}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {!acc ? (
        <div className="rounded-xl border border-dashed border-[#EBEBEA] p-8 text-center text-sm text-[#8A9992]">
          Elige una cuenta para ver el mes actual vs el anterior
        </div>
      ) : (
        <>
          {/* Delta summary bar */}
          <div className="rounded-xl border border-[#EBEBEA] bg-[#FAFAF9] p-4 grid grid-cols-3 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Ingresos</div>
              <div className="mt-1 flex items-center gap-2">
                <div className="font-display text-lg font-semibold tabular-nums text-[#1C7C54]">
                  {formatCurrency(curData?.income || 0, acc.currency)}
                </div>
                <DeltaBadge current={curData?.income || 0} previous={prevData?.income || 0} testid="mvm-delta-income" />
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Gastos</div>
              <div className="mt-1 flex items-center gap-2">
                <div className="font-display text-lg font-semibold tabular-nums text-[#D95D39]">
                  {formatCurrency(curData?.expense || 0, acc.currency)}
                </div>
                <DeltaBadge current={curData?.expense || 0} previous={prevData?.expense || 0} testid="mvm-delta-expense" />
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Movimientos</div>
              <div className="mt-1 flex items-center gap-2">
                <div className="font-display text-lg font-semibold tabular-nums text-[#0A110E]">
                  {curData?.count ?? 0}
                </div>
                <DeltaBadge current={curData?.count || 0} previous={prevData?.count || 0} testid="mvm-delta-count" />
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-stretch gap-4 md:gap-2">
            <div className="flex flex-col gap-4 flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992] capitalize">
                {monthLabel(0)} · actual
              </div>
              <SummaryCards acc={acc} data={curData} showBalance={false} />
            </div>
            <div className="hidden md:flex items-center px-2">
              <ArrowRight className="w-5 h-5 text-[#8A9992]" />
            </div>
            <div className="flex flex-col gap-4 flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992] capitalize">
                {monthLabel(-1)} · anterior
              </div>
              <SummaryCards acc={acc} data={prevData} showBalance={false} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function CompareAccountsDialog({ open, onOpenChange, accounts }) {
  const [mode, setMode] = useState("two"); // "two" | "mvm"
  const [left, setLeft] = useState(null);
  const [right, setRight] = useState(null);
  const [mvmAcc, setMvmAcc] = useState(null);
  const [preset, setPreset] = useState("90d");
  const [range, setRange] = useState(() => RANGE_PRESETS[1].get());

  useEffect(() => {
    if (!open) return;
    if (accounts?.length >= 2) {
      setLeft(accounts[0].id);
      setRight(accounts[1].id);
    }
    if (accounts?.length >= 1) setMvmAcc(accounts[0].id);
  }, [open, accounts]);

  const applyPreset = (id) => {
    setPreset(id);
    const p = RANGE_PRESETS.find((x) => x.id === id);
    if (p) setRange(p.get());
  };

  const setCustom = (r) => { setPreset("custom"); setRange(r); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl bg-white max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <GitCompare className="w-5 h-5 text-[#1C7C54]" /> Comparar cuentas
          </DialogTitle>
        </DialogHeader>

        {/* Mode toggle */}
        <div
          data-testid="compare-mode-toggle"
          className="inline-flex items-center gap-1 rounded-xl bg-[#FAFAF9] border border-[#EBEBEA] p-1 w-fit"
        >
          <button
            type="button"
            data-testid="compare-mode-two"
            onClick={() => setMode("two")}
            className={`h-8 px-3 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
              mode === "two" ? "bg-[#0F291E] text-white" : "text-[#5F7368] hover:bg-[#EBEBEA]"
            }`}
          >
            <Users className="w-3.5 h-3.5" /> Dos cuentas
          </button>
          <button
            type="button"
            data-testid="compare-mode-mvm"
            onClick={() => setMode("mvm")}
            className={`h-8 px-3 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
              mode === "mvm" ? "bg-[#0F291E] text-white" : "text-[#5F7368] hover:bg-[#EBEBEA]"
            }`}
          >
            <Repeat className="w-3.5 h-3.5" /> Mes vs mes
          </button>
        </div>

        {mode === "two" ? (
          <>
            <DateRangePicker
              preset={preset}
              onPreset={applyPreset}
              start={range.start}
              end={range.end}
              onCustom={setCustom}
            />
            <div className="flex flex-col md:flex-row items-stretch gap-4 md:gap-2 py-2">
              <AccountColumn accounts={accounts} selected={left} onSelect={setLeft} label="Izquierda" range={range} />
              <div className="hidden md:flex items-center px-2">
                <ArrowRight className="w-5 h-5 text-[#8A9992]" />
              </div>
              <AccountColumn accounts={accounts} selected={right} onSelect={setRight} label="Derecha" range={range} />
            </div>
          </>
        ) : (
          <MonthVsMonthView accounts={accounts} selected={mvmAcc} onSelect={setMvmAcc} />
        )}
      </DialogContent>
    </Dialog>
  );
}
